<template>
  <div ref="tableContainer" class="table-container" :class="{
    'scrolled-vertical': isScrolledVertically,
    'scrolled-horizontal': isScrolledHorizontally
  }">
    <table class="custom-table">
      <thead>
        <tr>
          <th v-for="(column, index) in columns" :key="column.key" :class="{
            'sticky-column': index === 0 && stickyFirstColumn,
            'sortable': column.sortable,
            'sorted': sortBy === column.sortKey
          }" :style="{ width: column.width, maxWidth: column.width, minWidth: column.width }" @click="column.sortable && handleSort(column.sortKey)">
            <div class="th-content" :class="{ 'th-sortable': column.sortable }">
              <span class="th-label">{{ column.label }}</span>
              <span v-if="column.sortable" class="sort-indicator">
                <svg v-if="sortBy === column.sortKey && sortOrder === 'asc'" width="16" height="16" viewBox="0 0 24 24">
                  <path d="M7 10l5-5 5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 14l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.3"/>
                </svg>
                <svg v-else-if="sortBy === column.sortKey && sortOrder === 'desc'" width="16" height="16" viewBox="0 0 24 24">
                  <path d="M7 10l5-5 5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" opacity="0.3"/>
                  <path d="M7 14l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" class="sort-inactive">
                  <path d="M7 10l5-5 5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M7 14l5 5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody v-if="rows.length > 0 && !isEmpty">
        <tr v-for="(row, rowIndex) in rows" :key="rowIndex" class="data-row" :class="rowClass ? rowClass(row, rowIndex) : ''">
          <td v-for="(column, colIndex) in columns" :key="column.key" :class="{
            'sticky-column': colIndex === 0 && stickyFirstColumn
          }" :style="{ width: column.width, maxWidth: column.width, minWidth: column.width }" @click="handleCellClick(rowIndex, colIndex)">
            <slot :name="`column-${column.key}`" :row="row" :rowIndex="rowIndex">
              {{ row[column.key] || '' }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-overlay" :style="loadingOverlayStyle">
      <div class="spinner"></div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && isEmpty" class="empty-state-content">
      <svg class="empty-state-icon" viewBox="0 0 184 152" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fill-rule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse fill-opacity=".8" fill="#F5F5F7" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
            <path
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
              fill="#AEB8C2" />
            <path
              d="M101.537 86.214L80.63 61.102c-1.001-1.207-2.507-1.867-4.048-1.867H31.724c-1.54 0-3.047.66-4.048 1.867L6.769 86.214v13.792h94.768V86.214z"
              fill="url(#linearGradient-1)" transform="translate(13.56)" />
            <path d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
              fill="#F5F5F7" />
            <path
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
              fill="#DCE0E6" />
          </g>
          <path
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
            fill="#DCE0E6" />
          <g transform="translate(149.65 15.383)" fill="#FFF">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
        <defs>
          <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="linearGradient-1">
            <stop stop-color="#FFF" stop-opacity=".5" offset="0%" />
            <stop stop-color="#F9FAFB" offset="100%" />
          </linearGradient>
        </defs>
      </svg>
      <h3 class="empty-state-title">No records found</h3>
      <p class="empty-state-description">There are no records to display at this time</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomTable',
  props: {
    columns: {
      type: Array,
      required: true,
      validator: (value) => {
        return Array.isArray(value) && value.every(col =>
          col && typeof col.key === 'string' && typeof col.label === 'string' && typeof col.width === 'string'
        );
      }
    },
    rows: {
      type: Array,
      required: true
    },
    stickyFirstColumn: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isEmpty: {
      type: Boolean,
      default: false
    },
    sortBy: {
      type: String,
      default: null
    },
    sortOrder: {
      type: String,
      default: 'desc'
    },
    rowClass: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      isScrolledVertically: false,
      isScrolledHorizontally: false,
      emptyStatePosition: {
        top: 0,
        left: 0,
        width: 0,
        height: 0
      },
      loadingPosition: {
        top: 0,
        left: 0,
        width: 0,
        height: 0
      }
    }
  },
  computed: {
    loadingOverlayStyle() {
      return {
        position: 'fixed',
        top: `${this.loadingPosition.top}px`,
        left: `${this.loadingPosition.left}px`,
        width: `${this.loadingPosition.width}px`,
        height: `${this.loadingPosition.height}px`
      }
    }
  },
  watch: {
    rows: {
      handler() {
        this.$nextTick(() => {
          this.updateEmptyStatePosition()
        })
      },
      immediate: false
    },
    isEmpty: {
      handler() {
        this.$nextTick(() => {
          this.updateEmptyStatePosition()
        })
      },
      immediate: false
    }
  },
  mounted() {
    this.handleScroll()
    this.updateEmptyStatePosition()
    this.updateLoadingPosition()
    this.$refs.tableContainer.addEventListener('scroll', this.handleScroll)
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    this.$refs.tableContainer.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleScroll() {
      const container = this.$refs.tableContainer
      if (!container) return

      this.isScrolledVertically = container.scrollTop > 0
      this.isScrolledHorizontally = container.scrollLeft > 0
    },
    handleResize() {
      this.handleScroll()
      this.updateEmptyStatePosition()
      this.updateLoadingPosition()
    },
    updateEmptyStatePosition() {
      const container = this.$refs.tableContainer
      if (!container) return

      const rect = container.getBoundingClientRect()
      const headerHeight = 50

      this.emptyStatePosition = {
        top: rect.top + headerHeight,
        left: rect.left,
        width: rect.width,
        height: rect.height - headerHeight
      }
    },
    updateLoadingPosition() {
      const container = this.$refs.tableContainer
      if (!container) return

      const rect = container.getBoundingClientRect()

      this.loadingPosition = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      }
    },
    handleCellClick(rowIndex, columnIndex) {
      this.$emit('row-click', { rowIndex, columnIndex });
    },
    handleSort(sortKey) {
      if (!sortKey) return;

      let newOrder = 'desc';
      if (this.sortBy === sortKey) {
        // Toggle order if same column
        newOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
      }

      this.$emit('sort', { sortBy: sortKey, sortOrder: newOrder });
    }
  }
}
</script>

<style scoped>
.table-container {
  width: 100%;
  height: 100%;
  overflow: auto;
  position: relative;
}

.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
}

/* Header styles */
.custom-table thead {
  position: sticky;
  top: 0;
  z-index: 5;
}

.custom-table thead th {
  background-color: var(--content-header-bg);
  border-bottom: var(--content-header-border);
  font-weight: 600;
  text-align: left;
  padding: 0 16px;
  white-space: nowrap;
  height: 50px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Show shadow on header when scrolled vertically */
.scrolled-vertical .custom-table thead th {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

/* Partial-height border between header cells */
.custom-table thead th:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 25%;
  height: 50%;
  width: 1px;
  background-color: var(--content-header-stroke);
}

/* Sortable column styles */
.th-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.th-content.th-sortable {
  justify-content: space-between;
}

.th-label {
  flex: 1;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sort-indicator {
  display: flex;
  align-items: center;
  color: #9ca3af;
  margin-left: 4px;
}

.sortable:hover .sort-indicator {
  color: #6b7280;
}

.sorted .sort-indicator {
  color: var(--primary-color);
}

.sort-inactive {
  opacity: 0.5;
}

/* Sticky first column */
.sticky-column {
  position: sticky;
  left: 0;
  z-index: 3;
  background-color: #fff;
}

.custom-table thead .sticky-column {
  position: sticky;
  z-index: 6;
  background-color: var(--content-header-bg);
}

/* Show shadow on sticky column when scrolled horizontally */
.scrolled-horizontal .sticky-column {
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
}

/* Show combined shadow on sticky header cell when scrolled both ways */
.scrolled-vertical.scrolled-horizontal .custom-table thead .sticky-column {
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.06);
}

/* Body rows */
.custom-table tbody {
  position: relative;
  z-index: 1;
}

.custom-table tbody td {
  padding: 0 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
  height: 50px;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-row {
  cursor: pointer;
}

.data-row:hover td {
  background-color: #f9fafb;
}

/* Empty state styles */
.empty-state-content {
  position: fixed;
  top: v-bind('emptyStatePosition.top + "px"');
  left: v-bind('emptyStatePosition.left + "px"');
  width: v-bind('emptyStatePosition.width + "px"');
  height: v-bind('emptyStatePosition.height + "px"');
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  z-index: 5;
  pointer-events: none;
  background-color: #fff;
}

.empty-state-icon {
  width: 180px;
  height: auto;
  margin-bottom: 24px;
  opacity: 0.8;
}

.empty-state-title {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.empty-state-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Loading overlay styles */
.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
  pointer-events: none;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f4f6;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
